/**
 * ADR-033 — shared break-segment helpers for the worker-portal attendance
 * surfaces (Clock In/Out + Dashboard Today card). Web mirror of
 * svdy-mobile/src/attendance/breaks.ts so web and mobile render the worker's
 * lunch/breaks identically (svdy-rules §10.5: single behavior contract).
 *
 * Break times are derived client-side from the day's break_out/break_in
 * punches (punch_type is server-classified by the configured break window).
 */
import type { MePunch } from '../api/me';
import { activeLocale } from './datetime';

export interface BreakSegment {
  out: string; // ISO datetime of break_out
  in: string | null; // ISO datetime of break_in, or null while on break
}

/** Pair break_out → break_in punches (time-ordered, valid only) into segments. */
export function deriveBreakSegments(punches: MePunch[]): BreakSegment[] {
  const valid = punches
    .filter((p) => p.is_valid !== false && !!p.punched_at)
    .slice()
    .sort((a, b) => ((a.punched_at ?? '') < (b.punched_at ?? '') ? -1 : 1));
  const segs: BreakSegment[] = [];
  for (const p of valid) {
    if (p.punch_type === 'break_out') {
      segs.push({ out: p.punched_at, in: null });
    } else if (p.punch_type === 'break_in' && segs.length && !segs[segs.length - 1].in) {
      segs[segs.length - 1].in = p.punched_at;
    }
  }
  return segs;
}

/** Browser-local HH:MM (matches fmtLocalTime used for clock_in/clock_out). */
export function fmtBreakHHMM(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleTimeString(activeLocale(), { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** "12:03–12:47   from 14:06" — a closed break shows the range; an OPEN break
 * (break_out with no break_in) shows "from <start>" so it's clear the end
 * wasn't recorded. ``fromLabel`` localizes the open case ("from {{time}}"). */
export function breakSummary(
  segs: BreakSegment[],
  fromLabel: (time: string) => string,
): string {
  return segs
    .map((s) => (s.in ? `${fmtBreakHHMM(s.out)}–${fmtBreakHHMM(s.in)}` : fromLabel(fmtBreakHHMM(s.out))))
    .join('   ');
}
